// New city alerts (docs/APP_REFERENCE.md "New city alerts"): a notification when a city arrives, for the people who ask
// for one, since the What's new pigeon only reaches people already here. Turning it on (Settings, or the one-time offer
// a few minutes into a visit; never on load) asks the browser for permission, subscribes with the site's VAPID key and
// hands the live worker (worker/alerts.js) the subscription: its push service's address and two keys, nothing about the
// person. Turning it off takes it back. The service worker shows each alert and opens its city (site/js/alerts-sw.js);
// the private admin page sends them. This browser's own state is `lofi-cities:alerts` ({ endpoint } while on), never
// synced to an account: each browser asks for itself. Pure helpers, plus createAlerts handed the page's parts, so node
// can test it: node --test tests/

// The application server's public key (P-256, uncompressed, base64url). Its private half is the live worker's secret
// VAPID_PRIVATE_KEY (docs/CLOUDFLARE_DEPLOYMENT.md "New city alerts"): a new pair means everyone subscribes again.
export const VAPID_PUBLIC_KEY = 'BIt6UpaSwGGRql-fFBf46u-bPMhtOWF5-_1aWj-U9pHdkWIKAnlp1fCbNwgYRbOzsN2zrmNBFvzxfzrcm0zB3JI';
export const TITLE_MAX = 60, BODY_MAX = 160;   // an alert's title and text, as the admin page writes them (worker/alerts.js)
export const OFFER_AFTER_MS = 4 * 60000;       // the offer comes this far into a visit, once, in view and with no other toast
const RETRY_MS = 30000;                        // or this much later, when the tab is hidden or a toast is up
const READY_MS = 10000;                        // the service worker this slow to be ready: something's wrong
const SITE = 'https://live.loficities.com/alerts';
const DEV = 'http://localhost:8787/alerts';

/** The worker's /alerts for this page, or null where there's no live worker (OBS, deploy previews, the tools). */
export function alertsUrl(loc, obs) {
  if (obs) return null;
  if (loc.hostname === 'loficities.com') return SITE;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(loc.hostname);
  return local && new URLSearchParams(loc.search).has('live') ? DEV : null;
}

/**
 * What this browser can do: 'ok' (it has the Push API), 'install' (Safari on an iPhone or iPad outside a Home Screen
 * app: it has push only there, from iOS 16.4) or 'none'.
 */
export function supportOf(win) {
  const nav = win.navigator;
  if (nav && 'serviceWorker' in nav && 'PushManager' in win && 'Notification' in win) return 'ok';
  const ios = /iPad|iPhone|iPod/.test(nav?.userAgent ?? '') || (nav?.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  const installed = nav?.standalone === true || win.matchMedia?.('(display-mode: standalone)').matches === true;
  return ios && !installed ? 'install' : 'none';
}

export const NOTES = {
  off: 'A notification when a new city arrives, and nothing else. No account needed.',
  on: 'On in this browser: you’ll hear when a new city arrives.',
  turningOn: 'Asking your browser…',
  turningOff: 'Turning off…',
  blocked: 'Notifications are blocked for this site. Allow them in your browser’s site settings, then turn this on.',
  install: 'On iPhone and iPad, add Lofi Cities to your Home Screen first (Share, then Add to Home Screen), open it from there and turn this on. Needs iOS 16.4 or later.',
  none: 'This browser can’t show notifications from websites.',
  failed: 'That didn’t work. Try again in a moment.',
};

/**
 * The Settings row: { state, checked, disabled, note } from the browser's `support` (supportOf), its notification
 * `permission`, whether alerts are `on` here, which way a change is `busy` going ('on', 'off' or null) and whether the
 * last one `failed`. state is one of off, on, busy, blocked, install, none.
 */
export function alertsView({ support, permission, on, busy = null, failed = false }) {
  if (support !== 'ok') return { state: support, checked: false, disabled: true, note: NOTES[support] };
  if (busy) return { state: 'busy', checked: busy === 'on', disabled: true, note: busy === 'on' ? NOTES.turningOn : NOTES.turningOff };
  if (permission === 'denied') return { state: 'blocked', checked: false, disabled: true, note: NOTES.blocked };
  if (on) return { state: 'on', checked: true, disabled: false, note: NOTES.on };
  return { state: 'off', checked: false, disabled: false, note: failed ? NOTES.failed : NOTES.off };
}

const keyBytes = () => Uint8Array.from(atob(VAPID_PUBLIC_KEY.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
/** Whether a subscription's applicationServerKey (an ArrayBuffer, or null) is the site's. */
export function sameKey(key) {
  const mine = keyBytes(), theirs = key ? new Uint8Array(key) : null;
  return !!theirs && theirs.length === mine.length && theirs.every((b, i) => b === mine[i]);
}

/**
 * The alerts for this page. `url` is alertsUrl's (null hides the row and does nothing); `row`, `input` and `note` are
 * the Settings switch's label, checkbox and note; `store`, `track`, `trackError` and `toast` are the page's; `win` its
 * window (or a stand-in). Returns { render, toggle, start }.
 */
export function createAlerts({ url, row, input, note, store, track, trackError, toast, win = globalThis }) {
  const support = url ? supportOf(win) : null;
  // The alert links, read as the page opens: a city page's first city rewrites the address before start() runs.
  const arrived = new URL(win.location.href).searchParams, from = arrived.get('from'), token = arrived.get('alert-test');
  let on = !!store.get('alerts', null)?.endpoint, busy = null, failed = false, pairing = null;
  const permission = () => win.Notification?.permission ?? 'default';
  const view = () => alertsView({ support, permission: permission(), on, busy, failed });

  function render() {
    row.hidden = !url;
    if (!url) return;
    const v = view();
    input.checked = v.checked;
    input.disabled = v.disabled;
    note.textContent = v.note;
    row.dataset.state = v.state;
  }

  async function post(path, body) {
    const res = await win.fetch(`${url}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const answer = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(`alerts/${path}: ${res.status} ${answer.why ?? ''}`.trim()), { why: answer.why });
    return answer;
  }
  // The service worker (registered on load, js/app.js); it never comes where none can run.
  const registration = () => new Promise((resolve, reject) => {
    const late = setTimeout(() => reject(new Error('alerts: no service worker')), READY_MS);
    win.navigator.serviceWorker.ready.then(r => { clearTimeout(late); resolve(r); }, e => { clearTimeout(late); reject(e); });
  });

  // A test link from the admin page (?alert-test=…) makes this browser a test device, once alerts are on here.
  async function pair() {
    const token = pairing;
    pairing = null;
    try {
      await post('test-device', { endpoint: store.get('alerts', null)?.endpoint, token });
      toast('This browser gets the admin page’s test alerts now', 6000);
    } catch (e) {
      toast(e.why === 'token' ? 'That test link has expired. Make a new one on the admin page.' : 'This browser couldn’t be made a test device. Try the link again.', 6000);
    }
  }

  async function turnOn(trigger) {
    if (busy || on || support !== 'ok') return;
    // Asked first, inside the click or tap itself: Safari and Firefox only prompt from a user's gesture.
    const asked = Promise.resolve(win.Notification.requestPermission());
    busy = 'on';
    failed = false;
    render();
    try {
      if (await asked !== 'granted') return;       // dismissed, or blocked: the row says which
      const { pushManager } = await registration();
      let sub = await pushManager.getSubscription();
      if (sub && !sameKey(sub.options?.applicationServerKey)) { await sub.unsubscribe(); sub = null; }
      sub ??= await pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes() });
      await post('subscribe', sub.toJSON());
      on = true;
      store.set('alerts', { endpoint: sub.endpoint });
      track('alerts_opted_in', { trigger });
    } catch (e) {
      failed = true;
      trackError(e, { feature: 'alerts', step: 'on' });
    } finally {
      busy = null;
      render();
    }
    if (on && pairing) await pair();
  }

  // Off for good: the browser's subscription goes even when the worker can't be told (it then forgets it at the next
  // alert, when the push service answers 410), so nothing more arrives either way.
  async function turnOff(trigger) {
    if (busy || !on) return;
    busy = 'off';
    failed = false;
    render();
    try {
      const sub = await (await registration()).pushManager.getSubscription();
      const endpoint = sub?.endpoint ?? store.get('alerts', null)?.endpoint;
      if (endpoint) await post('unsubscribe', { endpoint }).catch(e => trackError(e, { feature: 'alerts', step: 'unsubscribe' }));
      await sub?.unsubscribe();
      on = false;
      store.set('alerts', null);
      track('alerts_opted_out', { trigger });
    } catch (e) {
      failed = true;
      trackError(e, { feature: 'alerts', step: 'off' });
    } finally {
      busy = null;
      render();
    }
  }

  /** The switch was flipped (`checked`): on or off, from `trigger`. Settles when the browser and the worker have answered. */
  function toggle(checked, trigger) {
    const done = checked ? turnOn(trigger) : turnOff(trigger);
    render();
    return done;
  }

  // On a later visit: the browser may have dropped the subscription (permission taken back, an expired one) or made a
  // new one (pushsubscriptionchange). The row follows what's true, and the worker hears the new address.
  async function check() {
    if (!on || support !== 'ok') return;
    try {
      const sub = await (await registration()).pushManager.getSubscription();
      const was = store.get('alerts', null)?.endpoint;
      if (!sub || permission() !== 'granted') {
        on = false;
        store.set('alerts', null);
      } else if (sub.endpoint !== was) {
        await post('subscribe', { ...sub.toJSON(), old: was });
        store.set('alerts', { endpoint: sub.endpoint });
      }
    } catch (e) { trackError(e, { feature: 'alerts', step: 'check' }); }
    render();
  }

  // The offer: once per browser, OFFER_AFTER_MS into a visit, to someone who could say yes (supported, never asked).
  const offerable = () => !!url && view().state === 'off' && permission() === 'default' && !store.get('alerts-offered', false);
  function offerLater(quiet, wait = OFFER_AFTER_MS) {
    if (!offerable()) return;
    setTimeout(() => {
      if (!offerable()) return;
      if (!quiet()) return offerLater(quiet, RETRY_MS);
      store.set('alerts-offered', true);
      toast('New cities arrive every few weeks. Get a notification when one does?', 15000, null,
        [{ label: 'Turn on', run: () => turnOn('offer') }, { label: 'Not now', run: () => {} }]);
    }, wait);
  }

  /**
   * After the page's first city: acts on the alert links it opened with (?from=alert, the service worker's new tab;
   * ?alert-test=, the admin page's test link) and drops them from the address if they're still there, listens for a
   * click on an alert in a tab already open on its city, checks the subscription and times the offer (`quiet`: whether
   * a toast may show now).
   */
  function start({ city, quiet }) {
    if (from === 'alert') track('alert_clicked', { city, tab: 'new' });
    const address = new URL(win.location.href);
    if (address.searchParams.has('from') || address.searchParams.has('alert-test')) {
      address.searchParams.delete('from');
      address.searchParams.delete('alert-test');
      win.history.replaceState(win.history.state, '', address.href);
    }
    win.navigator.serviceWorker?.addEventListener('message', e => {
      if (e.data?.t === 'alert-clicked') track('alert_clicked', { city: e.data.city, tab: 'open' });
    });
    if (url && token && /^[a-z0-9]{8,64}$/.test(token)) {
      pairing = token;
      if (on) pair();
      else if (support === 'ok' && permission() !== 'denied') toast('Turn on New city alerts to make this browser a test device', 0, null, { label: 'Turn on', run: () => turnOn('test') });
      else toast('This browser can’t get alerts, so it can’t be a test device', 6000);
    }
    check();
    offerLater(quiet);
  }

  render();
  return { render, toggle, start };
}
